import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  acceptDirectInvite,
  getHome,
  listIncomingInvitations,
  type IncomingDirectInvitation,
  type PortalTransaction,
} from '../api';
import { useAuth } from '../auth';
import { HomeQueue, IncomingInviteQueue } from '../TaskCard';
import { usePortalWorkspaceRefresh } from '../workspace-refresh';
import { revisionsOf, workspaceOf } from '../workspace';

export function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<PortalTransaction[]>([]);
  const [invitations, setInvitations] = useState<IncomingDirectInvitation[]>([]);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [viewerId, setViewerId] = useState(user?.uid ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const processing = items.some((item) => workspaceOf(item).evidenceProcessing.state !== 'IDLE');
  const visibleInvitations = invitations.filter((item) => !dismissedIds.includes(item.invitationId));

  const load = useCallback(() => {
    return Promise.all([getHome(), listIncomingInvitations()])
      .then(([home, inbox]) => {
        setItems(home.data.transactions);
        setViewerId(home.data.viewerId);
        setInvitations(inbox.data.invitations);
        setError(null);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : 'Could not load Home.');
      });
  }, []);

  usePortalWorkspaceRefresh(load, {
    processingState: processing ? 'FINALIZING' : 'IDLE',
    revisions: revisionsOf(items),
  });

  async function onAccept(invitationId: string) {
    setBusyId(invitationId);
    setError(null);
    try {
      const result = await acceptDirectInvite(invitationId);
      await load().catch(() => undefined);
      navigate(`/packproofs/${result.transactionId}`);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'This invitation is not available.');
      setBusyId(null);
    }
  }

  return (
    <>
      <p className="eyebrow">Home</p>
      <h1>What needs you?</h1>
      <p className="lede">Home is a task queue. One next action per PackProof. Capture happens on your phone; the browser stays the workspace.</p>
      {error ? <p className="error">{error}</p> : null}
      <div className="stack">
        <IncomingInviteQueue
          items={visibleInvitations}
          busyId={busyId}
          onAccept={(invitationId) => { void onAccept(invitationId); }}
          onDefer={(invitationId) => setDismissedIds((current) => current.includes(invitationId) ? current : [...current, invitationId])}
        />
        <HomeQueue items={items} viewerId={viewerId} allowEmpty={visibleInvitations.length === 0} />
      </div>
    </>
  );
}
